import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AddCrossIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { getObjectTotalTime } from "~/store/selectors/selector.additionalObjectData.jsx";
import { getStopwatchByParentTypeAndParentId } from "~/store/selectors/selector.stopwatches.jsx";
import TrackedTime from "./TrackedTime.jsx";
import useStopwatchModal from "../Hooks/useStopwatchModal.jsx";
import Stopwatch from "../Timer/Stopwatch.js";
import TimeRecordsListTaskModalContainer from "../TimeRecordForm/TimeRecordsListTaskModalContainer.jsx";
import TimeRecordTrackedItemModalFormContainer from "../TimeRecordForm/TimeRecordTrackedItemModalFormContainer.jsx";

const TimeTracking = (props) => {
  const { t } = useTranslation();
  const { parentId, parentType, projectId, isLoggedUserClient } = props;

  const stopwatch = useSelector((state) => getStopwatchByParentTypeAndParentId(state, parentType, parentId));

  const { openModalAndShowForm, stopwatchId, onCommit, isFormVisible, closeForm } = useStopwatchModal();

  const [showAddTimeRecordModal, setShowAddTimeRecordModal] = useState(false);
  const [showTrackedTimeModal, setShowTrackedTimeModal] = useState(false);
  const trackedTime = useSelector((state) => getObjectTotalTime(state, "Task", parentId));

  const addTimeRecord = useCallback((event) => {
    event.preventDefault();
    setShowAddTimeRecordModal(true);
  }, []);

  const showTrackedTime = useCallback((event) => {
    event.preventDefault();
    setShowTrackedTimeModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowAddTimeRecordModal(false);
    setShowTrackedTimeModal(false);
    closeForm();
  }, [closeForm]);

  useEffect(() => {
    if (trackedTime <= 0 && showTrackedTimeModal) {
      handleCloseModal();
    }
  }, [handleCloseModal, trackedTime, showTrackedTimeModal]);

  return (
    <>
      <div className="c-pill-box tw-mb-6 tw-bg-page-paper-main">
        <div className="c-pill-box__header">
          {!isLoggedUserClient && (
            <button
              className="c-pill-box__btn-cta tw-text-secondary tw-bg-transparent tw-border-none tw-inline-flex tw-p-0 tw-cursor-pointer"
              onClick={addTimeRecord}
            >
              <AddCrossIcon className="tw-fill-current tw-mr-1" /> {t("Time")}
            </button>
          )}
          <TrackedTime
            showModal={showTrackedTime}
            parentId={parentId}
            openForm={openModalAndShowForm}
            isLoggedUserClient={isLoggedUserClient}
          />
        </div>
        {!isLoggedUserClient && (
          <Stopwatch
            projectId={projectId}
            parentType={parentType}
            parentId={parentId}
            onCommit={onCommit}
            id={stopwatch ? stopwatch.id : 0}
            started_on={stopwatch ? stopwatch.started_on : 0}
            elapsed={stopwatch ? stopwatch.elapsed : 0}
            stopwatchLarge
            description={stopwatch?.description ? stopwatch.description : ``}
          />
        )}
        {isFormVisible ? (
          <TimeRecordTrackedItemModalFormContainer
            parentId={parentId}
            parentType="Task"
            projectId={projectId}
            handleDismiss={handleCloseModal}
            stopwatchId={stopwatchId}
            trackedTime={trackedTime}
            open={isFormVisible}
            disableUserSelection
            displaySuccessToast={false}
          />
        ) : null}
      </div>
      {showAddTimeRecordModal ? (
        <TimeRecordTrackedItemModalFormContainer
          handleDismiss={handleCloseModal}
          parentId={parentId}
          parentType="Task"
          projectId={projectId}
          trackedTime={trackedTime}
          open={showAddTimeRecordModal}
          displaySuccessToast={false}
        />
      ) : null}
      {showTrackedTimeModal ? (
        <TimeRecordsListTaskModalContainer
          taskId={parentId}
          projectId={projectId}
          handleDismiss={handleCloseModal}
          trackedTime={trackedTime}
          open={showTrackedTimeModal}
        />
      ) : null}
    </>
  );
};

TimeTracking.propTypes = {
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  projectId: PropTypes.number,
  isLoggedUserClient: PropTypes.bool.isRequired,
};

TimeTracking.defaultProps = {
  projectId: null,
};

export default TimeTracking;
